<script lang="ts">
  import { onMount } from 'svelte';
  import { setupProjectAnimations } from '$lib/utils/animations';
  import VideoPlayer from '$lib/components/VideoPlayer.svelte';
  import ProjectNav from '$lib/components/ProjectNav.svelte';
  import Footer from '$lib/components/Footer.svelte';
  import FeatureCell from '$lib/components/FeatureCell.svelte';
  import ProjectHeroFullWidth from '$lib/components/ProjectHeroFullWidth.svelte';
  import FullWidthImage from '$lib/components/FullWidthImage.svelte';
  import Quote from '$lib/components/Quote.svelte';
  import ChallengeSolution from '$lib/components/ChallengeSolution.svelte';
  import { projects, getAdjacentProjects } from '$lib/projects';
  import Credits from '$lib/components/Credits.svelte';
  let scrollY = 0;
  let translateY = 0;
  $: translateY = scrollY * 0.5;

  // Get the current project slug from the URL
  const currentSlug = 'self-driving-car';
  const navigation = getAdjacentProjects(currentSlug);

</script>

<svelte:window bind:scrollY />

<div class="min-h-screen bg-white">
  <ProjectHeroFullWidth
    title="Futures of Mobility"
    client="VW"
    industry="Automotive"
    product="Interaction prototypes for fully autonomous vehicles"
    heroMedia={{
      type: 'video',
      src: 'https://vz-6b226b53-3c8.b-cdn.net/defc863b-b8f3-4a50-991d-77b3bd98ea63/playlist.m3u8'
    }}
    metrics={[
      { value: 3, label: "Projects" },
      { value: 12, label: "Prototypes" },
      { value: 6, label: "Months Duration" }
    ]}
    roles={[
      "Creative Direction",
      "Strategy & Foresight",
      "Technical Implementation"
    ]}
  />

  <div class="container mx-auto px-8">
    <div class="space-y-2 mb-12 animate-on-view border-t border-b border-black/10 py-6" data-delay="1">
      <p class="large-text text-black/90">
        Shaping the future of car interactions with tactile, and testable prototypes.
      </p>
    </div>

    <ChallengeSolution
      challengeText="How can we reimagine interaction within fully autonomous cars, using smart materials and haptic navigation?"
      solutionText="Over three collaborations, we delivered tangible prototypes—from responsive materials to haptic interfaces—that influenced real product decisions inside VW's future mobility division."
    />

    <div class="container mx-auto px-8">
      <div class="mb-16 animate-on-view">
        <div class="grid grid-cols-12 gap-4">
          <p class="text-black/90 leading-relaxed col-start-2 col-span-10">
            Over the course of three intensive projects I led Studio NAND's collaboration with VW Future Center Europe to envision and implement the next generation of car-user interfaces. Each interaction concept was grounded in prototyping and iterative testing, resulting in tangible outputs that helped shape interior strategies for autonomous vehicles.
          </div>
        </div>
      </div>
   

    <!-- Approach & Ideation Diagrams -->
    <div class="w-full flex flex-col items-center my-12">
      <div class="mx-auto flex flex-col items-center animate-on-view">
        <img
          src="/projects/meta-materials/diagram.png"
          alt="Ideation process diagram"
          class="w-full object-cover"
          style="max-width: 100%; height: auto;"
        />
        <p class="text-sm text-gray-600 mt-2 text-center w-full">
          Future Cone (based on Voros)
        </p>
      </div>
    </div>
  </div>
  <div class="container mx-auto px-8">
    <div class="mb-16 animate-on-view">
      <div class="grid grid-cols-12 gap-4">
        <p class="text-black/90 leading-relaxed col-start-2 col-span-10">            
          I led an initial workshop using strategic foresight methods to map out possible, plausible, and desirable futures for autonomous mobility. This collaborative session helped the team identify opportunity spaces and align on a shared vision for the next phases of prototyping.
        </p>
        </div>
        
        <div class="w-1/2 mx-auto my-12 flex flex-col items-center animate-on-view">
          <img
            src="/projects/meta-materials/process.jpg"
            alt="Ideation process diagram"
            class="w-full object-cover"
            style="max-width: 100%; height: auto;"
          />
          <p class="text-sm text-gray-600 mt-2 text-center w-full">Ideation process diagram</p>
        </div>
        <div class="grid grid-cols-12 gap-4 animate-on-view">
        <p class="text-black/90 leading-relaxed col-start-2 col-span-10">
          Our work began with the guiding principle that future interfaces must be less about control and more about communication. As self-driving technology removes the steering wheel and traditional dashboard, a new type of passenger experience becomes possible—one built around emotional resonance, ambient feedback, and multi-sensory interaction.
        </p>

        </div>

        <div class="flex flex-col md:flex-row gap-8 my-12 items-center">
          <div class="flex-1 flex flex-col items-center">
            <img
              src="/projects/meta-materials/MS 4.png"
              alt="Acrylic panel, unlit"
              class="w-full object-cover"
              style="max-width: 100%; height: auto;"
            />
            <p class="text-sm text-gray-600 mt-2 text-center w-full">Acrylic panel, unlit</p>
          </div>
          <div class="flex-1 flex flex-col items-center">
            <img
              src="/projects/meta-materials/MS 7.png"
              alt="Acrylic panel, illuminated"
              class="w-full object-cover"
              style="max-width: 100%; height: auto;"
            />
            <p class="text-sm text-gray-600 mt-2 text-center w-full">Acrylic panel, illuminated</p>
          </div>
        </div>

        <div class="flex flex-col md:flex-row gap-8 my-12 items-center">
        <p class="text-black/90 leading-relaxed col-start-2 col-span-10 mt-4">
          In <em>Meta Materials</em>, we investigated how surfaces could become communicative media. By embedding shape-memory alloys and programmable light patterns into trim components, we created prototypes capable of shifting form and color in real time. These were designed to express car states such as charging, user recognition, or ambient modes like 'rest' or 'alert'. The goal was to offer feedback that users feel rather than decode.
        </p>
        </div>
        <div class="mx-auto my-8 animate-on-view">
          <video
          src="/projects/meta-materials/roof.mp4"
          autoplay
          loop
          muted
          class="w-full h-full object-cover"
        ></video>
          <p class="text-sm text-gray-600 mt-2 text-center w-full">Intatial aniamtions helped us with the ideation process</p>
        </div>
        

        <div class="grid grid-cols-12 gap-4">
        <p class="text-black/90 leading-relaxed col-start-2 col-span-10 mt-4">
          In Level-5 Interactions, we brought these individual interfaces together into a cohesive passenger journey. We designed ambient onboarding sequences, mood-setting features based on biometric and contextual cues, and interaction zoning that adapted to group dynamics in shared vehicles. Our iterative loop involved sketching, physical prototyping, embedded systems programming, and user testing in simulated car cabins.
        </p>
        </div>
        <div class="mx-auto my-8">
          <video
          src="/projects/meta-materials/Sun New Context-sm.mp4"
          autoplay
          loop
          muted
          class="w-full h-full object-cover"
        ></video>
          <p class="text-sm text-gray-600 mt-2 text-center w-full">Animation of ambient reactions</p>
        </div>

        <div class="grid grid-cols-12 gap-4">
        <p class="text-black/90 leading-relaxed col-start-2 col-span-10 mt-4">
          Beyond the prototypes, we delivered complete documentation including behavioral patterns, visual scripts, interaction maps, and feedback logic—tools that could be handed off to engineering and design teams for real-world application. This ensured that our work was not just inspirational but operational.
        </p>
        </div>
        <FullWidthImage
        src="/projects/meta-materials/T89A9950.jpg"
        alt="Production for the electronic components"
        caption="Production for the electronic components"
      />
        <div class="mx-auto my-8 animate-on-view">
          <video
          src="/projects/meta-materials/T89A8761-sm.mp4"
          autoplay
          loop
          muted
          class="w-full h-full object-cover"
        ></video>

        <div class="grid grid-cols-12 gap-4 border-t border-black/10 py-6 mt-12">
        <p class="text-black/90 leading-relaxed col-start-2 col-span-10 mt-4">
          These collaborations exemplified my hybrid approach to creative leadership: integrating speculative foresight with hands-on technical execution. As both project lead and creative director, I shaped not only the vision but the mechanics of each interaction, ensuring our prototypes lived up to their ambition—tactile, testable, and rooted in the realities of automotive design.
        </p>
        </div>
      </div>
    </div>
  </div>

  <!-- Image Gallery -->



  <div class="container mx-auto px-8">
    <h2 class="text-2xl font-light mt-8 mb-8">Key Features</h2>
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      <FeatureCell
        icon="touchpad"
        title="Haptical Navigation"
        description="Guiding without screens—using embedded haptics to communicate direction and alerts."
      />
      <FeatureCell
        icon="layers"
        title="Meta Materials"
        description="Using shape-memory alloys to signal states like rest, alert, or welcome."
      />
      <FeatureCell
        icon="activity"
        title="Emotional Interfaces"
        description="Creating emotional bonds through responsive tactile surfaces."
      />
      <FeatureCell
        icon="cpu"
        title="Feasibility First"
        description="Every prototype was built to test, not just imagine—ready to be experienced."
      />
    </div>
  </div>

  <Quote 
    text="These weren't just speculative visions—they were tactile, testable, and strategically grounded tools shaping real automotive futures."
  />

  <div class="container mx-auto px-8">
  <Credits 
    client={{
      label: "Client",
      name: "VW Future Center Europe"
    }}
    team={[
      { name: "Olivier Bau", role: "Technical Lead" },
      { name: "Brian Harms", role: "Creative Technologist" },
      { name: "Felix Krämer", role: "Design Lead" },
      { name: "Prof. Judith Glaser", role: "Research Lead" }
    ]}
  />
</div>

  {#if navigation}
    <ProjectNav 
      currentProject={{
        title: navigation.currentProject.title,
        category: navigation.currentProject.category,
        link: navigation.currentProject.link
      }}
      allProjects={projects}
    />
  {/if}

  <Footer />
</div>